import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Phone, Mail, Search, Save, Handshake, CheckCircle2, XCircle, Clock, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import MyEventSidebar from "./MyEventSidebar";

const TECHNICAL_RE = /^(test[_\s-]|iter\d+|fixture|demo[_\s-]|debug|__)/i;
const cleanName = (s) => {
  if (!s) return "Votre mariage";
  const c = String(s).replace(/^\s*(test[_\s-]+|demo[_\s-]+)/i, "").replace(/\s+[a-f0-9]{6,}$/i, "").trim();
  return TECHNICAL_RE.test(c) ? "Votre mariage" : c;
};
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); } catch { return iso; } };

const STATUS_CHIP = {
  pressenti: { cls: "text-amber-300 border-amber-300/40 bg-amber-300/5",   Icon: Clock },
  reserve:   { cls: "text-[#DCB457] border-[#DCB457]/50 bg-[#DCB457]/10",  Icon: CheckCircle2 },
  annule:    { cls: "text-red-300 border-red-300/40 bg-red-300/5",         Icon: XCircle },
};

export default function MyEventPro() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState(null); // assignId
  const [notesDraft, setNotesDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (statusFilter) p.set("status", statusFilter);
    try {
      const [lRes, sRes] = await Promise.all([
        api.get(`/next/myevent/${eventId}/pro?${p}`),
        api.get(`/next/myevent/${eventId}/pro/stats`),
      ]);
      setItems(lRes.data.items || []);
      setStats(sRes.data);
    } catch (e) { setErr(e?.response?.status || 500); }
  };

  useEffect(() => {
    if (!eventId) return;
    setLoading(true);
    api.get(`/next/myevent/${eventId}/dashboard`).then(r => setDashboard(r.data)).catch(() => {});
    load().finally(() => setLoading(false));
  }, [eventId]);

  useEffect(() => { if (eventId) load(); /* eslint-disable-next-line */ }, [q, statusFilter]);

  if (!user) return <Navigate to="/connexion" replace />;
  if (err === 403) return <ErrorScreen title="Accès refusé" text="Vous n'êtes pas membre de cet événement." testid="mypro-forbidden" />;
  if (err === 404) return <ErrorScreen title="Événement introuvable" text="Cet événement n'existe pas." testid="mypro-notfound" />;

  const openNotes = (a) => { setEditing(a.id); setNotesDraft(a.notes || ""); };
  const closeNotes = () => { setEditing(null); setNotesDraft(""); };
  const saveNotes = async () => {
    setSaving(true);
    try {
      await api.patch(`/next/myevent/${eventId}/pro/${editing}/notes`, { notes: notesDraft });
      toast.success("Notes enregistrées.");
      closeNotes();
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
    finally { setSaving(false); }
  };

  const ev = dashboard?.event || {};
  const couple = cleanName(ev.name);

  return (
    <div className="min-h-screen bg-black text-white pb-20" data-testid="myevent-pro">
      <header className="border-b border-[#DCB457]/15 bg-black/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <Link to={`/myevent/${eventId}`} data-testid="mypro-back" className="text-xs uppercase tracking-widest text-white/60 hover:text-[#DCB457] inline-flex items-center gap-2">
            <ArrowLeft size={13} /> MyEvent
          </Link>
          <span className="text-[10px] uppercase tracking-[0.24em] text-[#DCB457] border border-[#DCB457]/40 px-2 py-1">MyPro</span>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-8 lg:pt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <MyEventSidebar />
        <main className="lg:col-span-9 min-w-0 space-y-8">
          <section>
            <p className="eyebrow">MyEvent · Module</p>
            <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">Votre <span className="italic gold-text">équipe.</span></h1>
            <p className="mt-3 text-white/60 text-sm md:text-base">Les professionnels qui vous accompagnent pour votre mariage.</p>
            {ev.date && <p className="mt-1 text-[#DCB457] text-sm" data-testid="mypro-event-summary">{couple} — {fmtDate(ev.date)}</p>}
          </section>

          <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="mypro-stats">
            <StatCard label="Total" value={stats?.total ?? 0} />
            <StatCard label="Confirmés" value={stats?.reserve ?? 0} accent />
            <StatCard label="Contactés" value={stats?.pressenti ?? 0} />
            <StatCard label="Annulés" value={stats?.annule ?? 0} />
          </section>

          <section className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="relative flex-1 max-w-md">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#DCB457]/60" />
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher par nom ou spécialité…" data-testid="mypro-search"
                className="w-full bg-black/60 border border-[#DCB457]/25 pl-9 pr-3 py-2.5 text-sm text-white placeholder-white/40 focus:border-[#DCB457] focus:outline-none" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="mypro-status-filter"
              className="bg-black/60 border border-[#DCB457]/25 px-3 py-2.5 text-sm text-white focus:border-[#DCB457] focus:outline-none">
              <option value="">Tous</option>
              <option value="reserve">Confirmés</option>
              <option value="pressenti">Contactés</option>
              <option value="annule">Annulés</option>
            </select>
          </section>

          <section data-testid="mypro-list">
            {loading ? (
              <p className="text-white/50 italic text-sm">Chargement…</p>
            ) : items.length === 0 ? (
              <div className="card-luxe p-8 text-center">
                <Handshake size={28} className="text-[#DCB457]/40 mx-auto mb-3" strokeWidth={1.2} />
                <p className="text-white/60 text-sm">
                  {q || statusFilter
                    ? "Aucun résultat."
                    : "Votre équipe se constitue. Nos organisateurs vous mettront en relation avec vos prestataires."}
                </p>
              </div>
            ) : (
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {items.map(a => {
                  const chip = STATUS_CHIP[a.status] || STATUS_CHIP.pressenti;
                  const Icon = chip.Icon;
                  const p = a.prestataire || {};
                  return (
                    <li key={a.id} data-testid={`mypro-card-${a.id}`}
                      className="card-luxe p-5 hover:border-[#DCB457] transition group">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          {p.specialty && <p className="text-[10px] uppercase tracking-widest text-[#DCB457]/70 mb-1">{p.specialty}</p>}
                          <p className="font-serif-luxe text-xl leading-tight truncate">{p.name}</p>
                          <div className="mt-3">
                            <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 border ${chip.cls}`}>
                              <Icon size={10} strokeWidth={1.6} /> {a.status_label}
                            </span>
                          </div>
                          {a.notes && (
                            <p className="mt-3 text-xs text-white/60 italic border-l border-[#DCB457]/30 pl-3 line-clamp-2">
                              « {a.notes} »
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="mt-4 flex items-center gap-2 flex-wrap">
                        {p.phone && (
                          <a href={`tel:${p.phone}`} data-testid={`mypro-phone-${a.id}`}
                            className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-[#DCB457] border border-white/15 hover:border-[#DCB457]/40 px-3 py-1.5">
                            <Phone size={11} /> Appeler
                          </a>
                        )}
                        {p.email && (
                          <a href={`mailto:${p.email}`} data-testid={`mypro-email-${a.id}`}
                            className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-[#DCB457] border border-white/15 hover:border-[#DCB457]/40 px-3 py-1.5">
                            <Mail size={11} /> Email
                          </a>
                        )}
                        <button onClick={() => openNotes(a)} data-testid={`mypro-notes-${a.id}`}
                          className="ml-auto text-[10px] uppercase tracking-widest text-[#DCB457] hover:underline">
                          {a.notes ? "Modifier mes notes" : "Ajouter des notes"}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </main>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center p-4 md:p-6" data-testid="mypro-notes-modal">
          <div className="card-luxe max-w-lg w-full p-6 md:p-8 relative">
            <button onClick={closeNotes} className="absolute top-4 right-4 text-white/40 hover:text-[#DCB457]" data-testid="mypro-notes-close">
              <X size={16} />
            </button>
            <p className="eyebrow">Mes notes</p>
            <h2 className="font-serif-luxe text-2xl mt-2 mb-4">Notes personnelles</h2>
            <p className="text-[11px] text-white/45 mb-3">Ces notes sont visibles uniquement par vous et votre co-organisateur.</p>
            <textarea rows={6} value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)}
              data-testid="mypro-notes-textarea" maxLength={2000}
              placeholder="Vos remarques, questions, préférences…"
              className="w-full bg-black/60 border border-[#DCB457]/25 px-3 py-2 text-sm text-white/85 focus:border-[#DCB457] focus:outline-none" />
            <div className="mt-5 flex gap-3">
              <button onClick={saveNotes} disabled={saving} data-testid="mypro-notes-save"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#DCB457] text-black hover:bg-[#DCB457]/90 text-xs uppercase tracking-widest disabled:opacity-50">
                <Save size={13} /> {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
              <button onClick={closeNotes} className="px-4 py-2.5 border border-white/20 text-white/70 hover:text-[#DCB457] text-xs uppercase tracking-widest">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="card-luxe p-4">
      <p className="eyebrow">{label}</p>
      <p className={`font-serif-luxe text-3xl mt-2 ${accent ? "gold-text" : ""}`}>{value}</p>
    </div>
  );
}
function ErrorScreen({ title, text, testid }) {
  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-8">
      <div className="max-w-md text-center" data-testid={testid}>
        <p className="eyebrow">MyPro</p>
        <h1 className="font-serif-luxe text-3xl mt-3 mb-3">{title}</h1>
        <p className="text-white/60 text-sm">{text}</p>
      </div>
    </div>
  );
}
